import { useState, useMemo } from 'react';
import {
	DndContext,
	closestCenter,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
} from '@dnd-kit/core';
import {
	SortableContext,
	sortableKeyboardCoordinates,
	verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import styled from 'styled-components';
import { RiArrowDownSLine, RiArrowUpSLine } from 'react-icons/ri';

import SortableLayerItem from './SortableLayerItem';
import useLayerReorder from '@hooks/useLayerReorder';

const LayerGroupHeader = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	cursor: pointer;
	user-select: none;
	font-size: 0.9rem;
	font-weight: 500;
	color: ${p => p.theme.colors.textAccent};
	text-transform: uppercase;
	letter-spacing: 0.5px;
	margin-bottom: 10px;
	padding: 4px 0;

	&:hover {
		color: ${p => p.theme.colors.textColor};
	}

	svg {
		font-size: 1.2rem;
	}
`;

const LayerGroupContent = styled.div`
	display: ${({ $isOpen }) => ($isOpen ? 'block' : 'none')};
	padding-left: 8px;
	border-left: 3px solid ${p => p.theme.colors.primary};
	margin-bottom: 16px;
`;

const LayerSublist = styled.ul`
	margin: 0;
	padding: 0;
	list-style: none;
	display: flex;
	flex-direction: column;
	row-gap: 14px;
`;

/**
 * @typedef {Object} LayerItem
 * @property {string} key - Unique identifier for the layer
 * @property {string} [layerId] - OpenLayers customLayerId (defaults to key)
 * @property {React.ReactNode} component - React component to render
 */

/**
 * Collapsible layer group with drag-and-drop reordering support.
 *
 * Integrates with OpenLayers map to:
 * - Detect when layers are ready (added to map)
 * - Update layer z-indices on reorder
 * - Show drag handles only for ready layers
 *
 * @param {Object} props
 * @param {string} props.title - Group header title (e.g., "IMAGERY", "ANALYSIS")
 * @param {LayerItem[]} props.layers - Array of layer objects to render
 * @param {Object} props.mapObject - OpenLayers map instance
 * @param {number} [props.baseZIndex=10] - Starting z-index for layers in this group
 * @param {boolean} [props.defaultOpen=true] - Whether group is expanded by default
 */
const SortableLayerGroup = ({ title, layers, mapObject, baseZIndex = 10, defaultOpen = true }) => {
	const [isOpen, setIsOpen] = useState(defaultOpen);

	const { handleReorder, getOrderedLayers, isLayerReady } = useLayerReorder(
		mapObject,
		layers,
		baseZIndex
	);

	// Configure drag sensors with activation constraints
	const sensors = useSensors(
		useSensor(PointerSensor, {
			activationConstraint: {
				distance: 5, // Require 5px movement before drag starts (prevents accidental drags)
			},
		}),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		})
	);

	/** Handle drag end - delegate to useLayerReorder hook */
	const handleDragEnd = event => {
		const { active, over } = event;
		if (over && active.id !== over.id) {
			handleReorder(active.id, over.id);
		}
	};

	const orderedLayers = getOrderedLayers(layers);

	// Include all layer IDs in SortableContext - disabled state is handled by useSortable
	const sortableIds = useMemo(() => {
		return orderedLayers.map(layer => layer.key);
	}, [orderedLayers]);

	if (layers.length === 0) return null;

	return (
		<div>
			<LayerGroupHeader onClick={() => setIsOpen(!isOpen)}>
				{isOpen ? <RiArrowUpSLine /> : <RiArrowDownSLine />}
				{title}
			</LayerGroupHeader>
			<LayerGroupContent $isOpen={isOpen}>
				<DndContext
					sensors={sensors}
					collisionDetection={closestCenter}
					onDragEnd={handleDragEnd}>
					<SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
						<LayerSublist>
							{orderedLayers.map(layer => (
								<SortableLayerItem
									key={layer.key}
									id={layer.key}
									isReady={isLayerReady(layer)}>
									{layer.component}
								</SortableLayerItem>
							))}
						</LayerSublist>
					</SortableContext>
				</DndContext>
			</LayerGroupContent>
		</div>
	);
};

export default SortableLayerGroup;
